import React, { useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { EmptyState, SectionCard, StatusChip } from '../ui';
import type { StatusTone } from '../ui';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { apiService } from '../../services/api';
import { tokens } from '../../theme/tokens';

export type CompatibilityVia =
  | 'native'
  | 'adapter'
  | 'filter_thread'
  | 'battery_model'
  | 'shoe_family'
  | 'manual';

export interface CompatibilityItem {
  id: string;
  name: string;
  brand: string;
  image: string | null;
  mount: string | null;
  via: CompatibilityVia;
}

export type CompatibilityGroupKey = 'lenses' | 'cameras' | 'accessories';

/** Shape of `compatibility` on `GET /products/:id` (only present with flag `lens_compatibility`). */
export interface ProductCompatibility {
  lenses: CompatibilityItem[];
  cameras: CompatibilityItem[];
  accessories: CompatibilityItem[];
  /** Per group: the product read returned only the first items; "Show all" loads the rest. */
  more?: Partial<Record<CompatibilityGroupKey, boolean>>;
}

interface ProductWorksWithProps {
  /** The product whose list "Show all" loads in full; without it the panel shows what the read sent. */
  productId?: string;
  compatibility?: ProductCompatibility | null;
  onOpenProduct: (item: CompatibilityItem) => void;
}

const VIA_TONE: Record<CompatibilityVia, StatusTone> = {
  native: 'positive',
  manual: 'positive',
  adapter: 'caution',
  filter_thread: 'info',
  battery_model: 'info',
  shoe_family: 'info',
};

const GROUPS: Array<{ key: CompatibilityGroupKey; label: string }> = [
  { key: 'lenses', label: 'lensCompatibility.lenses' },
  { key: 'cameras', label: 'lensCompatibility.cameras' },
  { key: 'accessories', label: 'lensCompatibility.accessories' },
];

/** Items per group before "Show all": three rows of two. */
export const WORKS_WITH_COLLAPSED = 6;

/** Items per page of the full list. */
const FULL_LIST_PAGE = 60;

/**
 * "Works with" on the store product details: lenses for a camera, cameras and
 * filters for a lens, matching batteries and flashes. Shown only with the
 * `lens_compatibility` flag on; with it off the section does not exist. The
 * server sends `compatibility` only when the flag is on, so the two agree.
 */
export function ProductWorksWith({ productId, compatibility, onOpenProduct }: ProductWorksWithProps) {
  const { enabled } = useOrgFeatureFlag('lens_compatibility');
  const { t } = useAppTranslation();

  if (!enabled || !compatibility) return null;

  const groups = GROUPS.filter((g) => (compatibility[g.key] ?? []).length > 0);

  return (
    <SectionCard dense padded title={t('lensCompatibility.title')} subtitle={t('lensCompatibility.subtitle')}>
      {groups.length === 0 ? (
        <EmptyState
          size="compact"
          title={t('lensCompatibility.empty')}
          description={t('lensCompatibility.emptyHint')}
        />
      ) : (
        <Stack spacing={2}>
          {groups.map((group) => (
            <WorksWithGroup
              key={group.key}
              productId={productId}
              group={group.key}
              label={t(group.label)}
              items={compatibility[group.key]}
              hasMore={!!compatibility.more?.[group.key]}
              onOpenProduct={onOpenProduct}
            />
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

interface WorksWithGroupProps {
  productId?: string;
  group: CompatibilityGroupKey;
  label: string;
  /** What the product read sent (at most its read cap). */
  items: CompatibilityItem[];
  /** The read sent only the first items of a longer list. */
  hasMore: boolean;
  onOpenProduct: (item: CompatibilityItem) => void;
}

/**
 * One group: collapsed to `WORKS_WITH_COLLAPSED`, "Show all" expands to what the
 * read sent, or, when the read was cut short, loads the full list page by page
 * from `GET /products/:id/compatibility` (only on that click, never on open).
 */
function WorksWithGroup({ productId, group, label, items, hasMore, onOpenProduct }: WorksWithGroupProps) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const loadsFullList = hasMore && !!productId;

  const full = useInfiniteQuery({
    queryKey: ['product-compatibility', productId, group],
    queryFn: ({ pageParam }) => apiService.getProductCompatibilityPage(productId!, group, pageParam, FULL_LIST_PAGE),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    enabled: open && loadsFullList,
    staleTime: 60_000,
  });

  const fullItems = full.data?.pages.flatMap((p) => p.items as CompatibilityItem[]);
  const total = full.data?.pages[0]?.total;
  const shown = !open ? items.slice(0, WORKS_WITH_COLLAPSED) : fullItems ?? items;
  const canExpand = items.length > WORKS_WITH_COLLAPSED || loadsFullList;

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Box
        sx={{
          mt: 0.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {shown.map((item) => (
          <WorksWithCard key={item.id} item={item} onOpenProduct={onOpenProduct} />
        ))}
      </Box>
      {open && full.isError ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {t('lensCompatibility.loadError')}
        </Typography>
      ) : null}
      {canExpand || (open && full.hasNextPage) ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          {canExpand ? (
            <Button size="small" aria-expanded={open} onClick={() => setOpen(!open)}>
              {open
                ? t('lensCompatibility.showFewer')
                : loadsFullList
                  ? t('lensCompatibility.showAllMore')
                  : t('lensCompatibility.showAll', { count: items.length })}
            </Button>
          ) : null}
          {open && full.hasNextPage ? (
            <Button size="small" onClick={() => full.fetchNextPage()} disabled={full.isFetchingNextPage}>
              {t('lensCompatibility.loadMore')}
            </Button>
          ) : null}
          {open && (full.isFetching || full.isFetchingNextPage) ? <CircularProgress size={16} /> : null}
          {open && fullItems && total !== undefined ? (
            <Typography variant="caption" color="text.secondary">
              {t('lensCompatibility.showingOf', { shown: fullItems.length, total })}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

function WorksWithCard({ item, onOpenProduct }: { item: CompatibilityItem; onOpenProduct: (item: CompatibilityItem) => void }) {
  const { t } = useAppTranslation();
  return (
    <ButtonBase
      onClick={() => onOpenProduct(item)}
      aria-label={t('lensCompatibility.open', { name: item.name })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: 1.25,
        p: 1,
        borderRadius: 1,
        border: `1px solid ${tokens.line.default}`,
        '&:hover': { bgcolor: tokens.surface.subtle },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: tokens.surface.sunken,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {item.image ? (
          <Box
            component="img"
            src={item.image}
            alt=""
            loading="lazy"
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : null}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={item.name}>
          {item.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {[item.brand, item.mount].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <StatusChip label={t(`lensCompatibility.via.${item.via}`)} tone={VIA_TONE[item.via] ?? 'neutral'} />
    </ButtonBase>
  );
}
